import styled from "styled-components";
import React, {useRef} from "react";
import {observer} from "mobx-react";
import {PageBackButtonComponent} from "../page-back-button/page-back-button.component";
import {StandardPageHeaderComponentProps} from "./standard-page-header.component.props";
import {shouldRenderTestModeMenu, TestModeMenuComponent} from "../../test-mode/test-mode-menu.component";
import {StandardPageHeaderBox} from "./standard-page-header-box";
import {useServices} from "../../../hooks/use-services.hook";
import {usePageHeader} from "../../../services/page-header/use-page-header.hook";
import {LanguageSwitcherComponent} from "../../../components/language-switcher/language-switcher.component";
import {
    LoggedInUserIndicatorComponent
} from "../../../components/logged-in-user-indicator/logged-in-user-indicator.component";
import {PageHeaderLogoComponent} from "./parts/page-header-logo.component";

const HeaderContainerBox = styled(StandardPageHeaderBox)`
    box-shadow: none;
    gap: ${props => props.theme.spacing.flexGap8};
`

export const TopSectionBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
`

const TopMiddleSectionBox = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex-grow: 1;
    font-size: ${props => props.theme.fontSize.h3};
    font-weight: ${props => props.theme.fontWeight.semiBold};
`

const TopLeftSectionBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: ${props => props.theme.spacing.flexGap8};
`

const TopRightSectionBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: ${props => props.theme.spacing.flexGap12};
`


const BottomSectionBox = styled.div`
    width: 100%;
`

export const StandardPageHeaderSmallScreenComponent: React.FC<StandardPageHeaderComponentProps> = observer((props) => {
    const services = useServices();
    const hideBackButton = Boolean(props.hideBackButton);
    const hideLogoButton = Boolean(props.hideLogo);
    const hideLanguageSwitcher = Boolean(props.hideLanguageSwitcher);
    const containerRef = useRef<HTMLIonHeaderElement | null>(null);

    usePageHeader(containerRef);


    const onLogoClick = async () => {
        if(props.onLogoButtonClick) {
            props.onLogoButtonClick();
        } else {
            await services.navigator.goHome();
        }
    }

    const renderBackButton = () => {
        if(hideBackButton) {
            return null;
        }

        return (
            <PageBackButtonComponent key={"BACK_BUTTON"}
                                     isHidden={hideBackButton}
                                     onClick={props.onBackButtonClick}
                                     useCloseButtonInsteadOfBackButton={props.useCloseButtonInsteadOfBackButton}/>
        );
    }

    const renderLogo = () => {
        if(hideLogoButton) {
            return null;
        }

        return (<PageHeaderLogoComponent key={"LOGO"} useLargeLogo={Boolean(props.useLargeLogoOnSmallScreen)} onClick={onLogoClick} />)

    }

    const renderLoggedInIndicator = () => {
        if(!services.user.isAuthorized) {
            return null;
        }

        return (<LoggedInUserIndicatorComponent key={"LOGGED_IN_INDICATOR"}/>)
    }

    const renderLanguageSwitcher = () => {
        if(hideLanguageSwitcher) {
            return null;
        }

        return (
            <LanguageSwitcherComponent key={"LANGUAGE_SWITCHER"}/>
        );
    }

    const renderBurgerMenu = () => {
        if(props.renderBurgerMenu) {
            const burgerMenu = props.renderBurgerMenu();
            if(!burgerMenu) {
                return null;
            }
            return (
                <React.Fragment key={"BURGER_MENU"}>
                    {burgerMenu}
                </React.Fragment>
            )
        }
        return null;
    }

    const renderTopMiddleContent = () => {
        if(props.renderTopMiddleSection) {
            return (
                <React.Fragment key={"TOP_MIDDLE_SECTION"}>
                    {props.renderTopMiddleSection()}
                </React.Fragment>
            )
        }
        return null;
    }

    const renderTestMenu = () => {
        if(!shouldRenderTestModeMenu(services)) {
            return null;
        }
        return (
            <TestModeMenuComponent key={"TEST_MENU"}/>
        )
    }

    const topLeftCornerParts = [
        renderBackButton(),
        renderLogo(),
    ].filter(part => Boolean(part));

    const topMiddleParts = [
        renderTopMiddleContent()
    ].filter(part => Boolean(part));

    const topRightCornerParts = [
        renderLoggedInIndicator(),
        renderLanguageSwitcher(),
        renderBurgerMenu(),
        renderTestMenu()
    ].filter(part => Boolean(part));

    const renderTopLeftSection = () => {
        if(topLeftCornerParts.length === 0) {
            return null;
        }

        return (
            <TopLeftSectionBox>
                {topLeftCornerParts.map(part => part)}
            </TopLeftSectionBox>
        );
    }

    const renderTopMiddleSection = () => {
        //if we don't have topMiddleParts, but we have left or right parts we want to render TopMiddleSectionBox to push them on the side (TopMiddleSectionBox has flex-grow: 1)
        if(topMiddleParts.length === 0 && topRightCornerParts.length === 0 && topLeftCornerParts.length === 0) {
            return null;
        }
        return (
            <TopMiddleSectionBox>
                {topMiddleParts.map(part => part)}
            </TopMiddleSectionBox>
        );
    }


    const renderTopRightSection = () => {
        if(topRightCornerParts.length === 0) {
            return null;
        }
        return (
            <TopRightSectionBox>
                {topRightCornerParts.map(part => part)}
            </TopRightSectionBox>
        );
    }

    const hasChildren = React.Children.count(props.children) > 0;

    const renderBottomSection = () => {
        if(hasChildren) {
            return (
                <BottomSectionBox>
                    {props.children}
                </BottomSectionBox>
            );
        }

        return null;
    }

    return (
        <HeaderContainerBox ref={containerRef} $addBottomPadding={Boolean(props.addBottomPadding) || !hasChildren}>
            <TopSectionBox>
                {renderTopLeftSection()}
                {renderTopMiddleSection()}
                {renderTopRightSection()}
            </TopSectionBox>
            {renderBottomSection()}
        </HeaderContainerBox>
    )
});
